import React, { useState } from "react";
import { ItemResults } from "./ItemResults";
import { ItemFilter, ItemFilterInitializer } from "../../../entities/filters/ItemFilter";
import { Loader } from "../../theming/loader/Loader";
import { ErrorNotification } from "../../notifications/Notification";
import { useAppDispatch, useAppSelector } from "../../../store/configureStore";
import { LocalCharacterInitializer } from "../../../entities/characters/Character";
import { useLocation, useNavigate } from "react-router";
import { CharacterSpecificActions } from "../../../store/stores/collection/characters/actions/CharacterSpecificActions";
import { ItemTypeFilter } from "./filters/ItemTypeFilter";
import { NameFilter } from "./filters/NameFilter";
import { ItemDescriptionFilter } from "./filters/ItemDescriptionFilter";
import { ItemWeightFilters } from "./filters/ItemWeightFilter";
import { TagsFilter } from "./filters/TagsFilter";
import { ItemCostFilter } from "./filters/ItemCostFilter";

type Props = {
    character?: LocalCharacterInitializer;
    useRoute?: boolean;
    close?: () => void;
}

export const ItemsView: React.FC<Props> = (props: Props) => {
    const { character, useRoute, close } = props;
    const { items: allItems, allItemsLoaded, itemsError } = useAppSelector(app => app.library);

    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const query = new URLSearchParams(location.search);
    const [filterState, setFilterState] = useState(ItemFilter.CreateFilterFromQuery(query));
    const itemFilter = new ItemFilter(filterState);

    const search = (newState: ItemFilterInitializer) => {
        if (useRoute) {
            itemFilter.update(newState);
            const query = itemFilter.generateQueryString();
            navigate(query, { replace: true });
        }
        setFilterState(newState);
        if (character != null) {
            dispatch(CharacterSpecificActions.filterItems(character, newState));
        }
    }
    
    const filteredItems = itemFilter.filter(allItems);
    return (
        <>
            {itemsError && <ErrorNotification>There was an error loading items, you can try refreshing the page to reload them.</ErrorNotification>}

            <div className="filters">
                <ItemTypeFilter types={filterState.types} onChange={(types) => search({ ...filterState, types })} />

                <NameFilter name={filterState.name} onChange={name => search({ ...filterState, name })} />

                <ItemDescriptionFilter description={filterState.description} onChange={description => search({ ...filterState, description })} />

                <ItemCostFilter minCost={filterState.minCost} maxCost={filterState.maxCost}
                    onChange={(minCost, maxCost) => search({ ...filterState, minCost, maxCost })} />

                <TagsFilter tags={filterState.tags} onChange={tags => search({ ...filterState, tags })} />

                <ItemWeightFilters minWeight={filterState.minWeight} maxWeight={filterState.maxWeight}
                    onChange={(minWeight, maxWeight) => search({ ...filterState, minWeight, maxWeight })} />
            </div>

            {allItemsLoaded && <ItemResults character={character} items={filteredItems} close={close} useRoute={useRoute} />}
            {!allItemsLoaded && !itemsError && <Loader>Loading Items...</Loader>}
        </>
    )
}